import { useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Upload, FileText, CheckCircle, Clock, Trash2, Plus, Minus, Percent, AlertTriangle, Undo2, Scale, Pencil } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { useAuth } from '../../lib/auth';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { FotoProduto } from '../../components/FotoProduto';
import { LightboxImagem } from '../atendimento/componentes/LightboxImagem';
import { formatMoeda, formatDataSP, custoRealDoItem, fatorRateioDaNota } from '@fiodecobre/shared';
import { ModalDivergenciaRecebimento } from './ModalDivergenciaRecebimento';
import { ModalDevolverAoFornecedor } from './ModalDevolverAoFornecedor';

const ULTIMA_REVISADA_KEY = 'notasEntrada.ultimaRevisada';

/** Custo com as 4 casas que a coluna `preco_custo` guarda — ver a previa da [DA-734]. */
function moeda4(v: number): string {
  return `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
}

interface Nota {
  id: number;
  numero: string;
  serie: string;
  fornecedor_id: number | null;
  fornecedor_nome: string | null;
  fornecedor_cnpj: string | null;
  fornecedor_fantasia: string | null;
  fornecedor_razao: string | null;
  fornecedor_uf: string | null;
  data_emissao: string | null;
  valor_total: string;
  outras_despesas: string | null;
  estoque_atualizado: boolean;
  tem_divergencia: boolean;
  /** Divergencias de recebimento ainda abertas (registradas e nao finalizadas pelo admin). */
  divergencias_abertas?: number;
  criado_em: string;
}

interface NotaItem {
  id: number;
  numero_item: number;
  produto_id: number | null;
  codigo_fornecedor: string | null;
  descricao: string | null;
  unidade: string | null;
  quantidade: string;
  qtd_real: string | null;
  valor_unitario: string;
  valor_total: string;
  valor_icms: string;
  valor_ipi: string;
  valor_icms_st: string;
  valor_difal: string;
  aliquota_icms: string;
  produto_descricao?: string | null;
  codigo_interno?: string | null;
  foto_url?: string | null;
  unidade_venda_sigla?: string | null;
  // [DA-559] Preenchidos so quando o item ja voltou pro fornecedor por devolucao AUTORIZADA.
  qtd_devolvida?: string | null;
  devolucao_nfe_numero?: number | null;
  devolucao_em?: string | null;
  // [DA-734] Estado de HOJE do produto. A correcao de quantidade nao mexe em estoque, entao a
  // tela precisa dizer quanto ele esta pra quem for acertar na ficha.
  produto_estoque_atual?: string | null;
  produto_preco_custo?: string | null;
}

interface DadosNota {
  valor_total?: string;
  valor_produtos?: string;
  outras_despesas?: string;
}

export function NotasEntradaLista() {
  const navigate = useNavigate();
  const toast = useToast();
  const dialog = useDialog();
  const [notas, setNotas] = useState<Nota[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [excluindo, setExcluindo] = useState<number | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => new Set());
  const [fotoAmpliada, setFotoAmpliada] = useState<{ url: string; legenda: string } | null>(null);
  const [itensPorNota, setItensPorNota] = useState<Record<number, NotaItem[] | null>>({});
  const [dadosNota, setDadosNota] = useState<Record<number, DadosNota>>({});
  const [itemSel, setItemSel] = useState<number | null>(null);
  const itemRefs = useRef<Record<number, HTMLTableRowElement | null>>({});
  const notaRefs = useRef<Record<number, HTMLTableRowElement | null>>({});
  const [ultimaRevisadaId, setUltimaRevisadaId] = useState<number | null>(() => {
    try { return Number(localStorage.getItem(ULTIMA_REVISADA_KEY)) || null; } catch { return null; }
  });
  const [divergenciaNotaId, setDivergenciaNotaId] = useState<number | null>(null);
  // [DA-559] Devolver mercadoria ao fornecedor.
  const [devolverNotaId, setDevolverNotaId] = useState<number | null>(null);

  // [DA-734] Correcao da quantidade de um item ja importado. `editandoQtd` guarda o item aberto e
  // o texto digitado; a previa do que vai mudar sai do proprio texto, entao ela acompanha a
  // digitacao e ninguem aplica sem ter visto o custo novo.
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin';
  const [editandoQtd, setEditandoQtd] = useState<{ itemId: number; notaId: number; texto: string } | null>(null);
  const [salvandoQtd, setSalvandoQtd] = useState(false);
  const [pendenciaEstoque, setPendenciaEstoque] = useState<Record<number, {
    produtoId: number | null; codigo: string | null; estoqueHoje: number;
    qtdAnterior: number; qtdNova: number; delta: number;
  }>>({});

  // [DA-679] Rateio de compra repartida em varias notas (paga + bonificacao). A selecao mora aqui
  // porque o agrupamento e' do OPERADOR: nada no XML diz que tres notas sao a mesma compra.
  const [selecionadas, setSelecionadas] = useState<Set<number>>(() => new Set());

  function alternarSelecao(id: number) {
    setSelecionadas((prev) => {
      const nova = new Set(prev);
      if (nova.has(id)) nova.delete(id); else nova.add(id);
      return nova;
    });
  }

  // O rateio e' de UMA compra: fornecedores diferentes nao se misturam, e nota que nao creditou
  // estoque nao gravou custo — nao ha o que corrigir nela. O backend recusa os dois casos; aqui a
  // tela so evita oferecer o que ele vai negar.
  const rateio = useMemo(() => {
    const escolhidas = notas.filter((n) => selecionadas.has(n.id));
    if (escolhidas.length < 2) {
      return { pronto: false, motivo: 'Marque 2 ou mais notas da mesma compra', escolhidas };
    }
    if (new Set(escolhidas.map((n) => n.fornecedor_cnpj ?? '')).size > 1) {
      return { pronto: false, motivo: 'As notas marcadas sao de fornecedores diferentes', escolhidas };
    }
    const semEstoque = escolhidas.filter((n) => !n.estoque_atualizado);
    if (semEstoque.length > 0) {
      return {
        pronto: false,
        escolhidas,
        motivo: `A nota ${semEstoque.map((n) => n.numero).join(', ')} nao atualizou o estoque`,
      };
    }
    return { pronto: true, motivo: '', escolhidas };
  }, [notas, selecionadas]);

  function abrirRevisarMargens(id: number) {
    try { localStorage.setItem(ULTIMA_REVISADA_KEY, String(id)); } catch {}
    setUltimaRevisadaId(id);
    navigate(`/notas-entrada/${id}/revisar-margens`);
  }

  const [searchParams] = useSearchParams();

  function carregarNotas() {
    return api.get<Nota[]>('/api/notas-entrada')
      .then((data) => setNotas(data))
      .catch(() => {})
      .finally(() => setCarregando(false));
  }

  useEffect(() => { void carregarNotas(); }, []);

  // A pastilha "Faltou item" do cabecalho cai aqui com ?divergencia=aberta: a lista mostra so as
  // notas com pendencia, e a faixa acima dela diz isso e oferece voltar pra lista inteira.
  const soDivergencia = searchParams.get('divergencia') === 'aberta';
  const notasVisiveis = soDivergencia ? notas.filter((n) => (n.divergencias_abertas ?? 0) > 0) : notas;
  const [finalizando, setFinalizando] = useState<number | null>(null);

  async function finalizarDivergencia(n: Nota) {
    const qtd = n.divergencias_abertas ?? 0;
    const ok = await dialog.confirmar(
      `Encerrar a pendencia de ${qtd} ${qtd === 1 ? 'item' : 'itens'} que faltaram na nota ${n.numero}? ` +
      'O estoque e o credito do fornecedor ficam como estao.',
      { titulo: 'Finalizar Divergencia' },
    );
    if (!ok) return;
    setFinalizando(n.id);
    try {
      await api.post(`/api/notas-entrada/${n.id}/divergencias/finalizar`, {});
      toast.sucesso(`Divergencia da nota ${n.numero} finalizada`);
      await carregarNotas();
      window.dispatchEvent(new Event('divergencias-recebimento:mudou'));
    } catch (e: any) {
      toast.erro(e?.message || 'Falha ao finalizar a divergencia');
    } finally {
      setFinalizando(null);
    }
  }

  // Deep-link ?nota=<id> — o link "ref. entrada" da devolucao, na tela de Notas Fiscais, cai aqui.
  // Sem isto o clique abriria a lista inteira e a nota referenciada continuaria escondida no meio.
  useEffect(() => {
    const alvo = Number(searchParams.get('nota'));
    if (!alvo || carregando || notas.length === 0) return;
    if (!notas.some((n) => n.id === alvo)) return;
    setExpandedIds((prev) => (prev.has(alvo) ? prev : new Set(prev).add(alvo)));
    if (itensPorNota[alvo] === undefined) void carregarItensDaNota(alvo);
    setTimeout(() => notaRefs.current[alvo]?.scrollIntoView({ block: 'center' }), 60);
    // Roda uma vez por alvo: sem a guarda, todo re-render voltaria o scroll pra la.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, carregando, notas.length]);

  const allVisibleItems = useMemo(() => {
    const ids: number[] = [];
    for (const n of notas) {
      if (!expandedIds.has(n.id)) continue;
      const itens = itensPorNota[n.id];
      if (!itens) continue;
      for (const it of itens) ids.push(it.id);
    }
    return ids;
  }, [notas, expandedIds, itensPorNota]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      if (allVisibleItems.length === 0) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      e.preventDefault();
      setItemSel(prev => {
        const idx = prev != null ? allVisibleItems.indexOf(prev) : -1;
        let next: number;
        if (e.key === 'ArrowDown') next = idx < allVisibleItems.length - 1 ? idx + 1 : idx;
        else next = idx > 0 ? idx - 1 : 0;
        const id = allVisibleItems[next]!;
        setTimeout(() => itemRefs.current[id]?.scrollIntoView({ block: 'nearest' }), 0);
        return id;
      });
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [allVisibleItems]);

  async function carregarItensDaNota(notaId: number) {
    setItensPorNota(prev => prev[notaId] !== undefined ? prev : { ...prev, [notaId]: null });
    try {
      const res = await api.get<{ itens: NotaItem[] } & DadosNota>(`/api/notas-entrada/${notaId}`);
      const { itens, ...resto } = res;
      setItensPorNota(prev => ({ ...prev, [notaId]: itens || [] }));
      setDadosNota(prev => ({ ...prev, [notaId]: resto }));
    } catch {
      setItensPorNota(prev => ({ ...prev, [notaId]: [] }));
    }
  }

  // [DA-734] Grava a quantidade corrigida. O backend refaz o custo com a MESMA peca do `shared`
  // que desenhou a previa — se as duas contas fossem separadas, o numero gravado poderia diferir
  // do que a tela mostrou e ninguem perceberia.
  async function aplicarQtd(notaId: number, itemId: number, nova: number) {
    setSalvandoQtd(true);
    try {
      const r = await api.patch<{
        qtd_anterior: number; qtd_nova: number;
        custo_anterior: number | null; custo_novo: number;
        custo_atual_veio_desta_nota: boolean; vinculos_corrigidos: number;
        estoque_atual: number | null; estoque_delta_sugerido: number;
        codigo_interno: string | null; produto_id: number | null;
      }>(`/api/notas-entrada/${notaId}/itens/${itemId}`, { qtd_real: nova });

      setEditandoQtd(null);
      await carregarItensDaNota(notaId);

      const partes = [`Quantidade ${r.qtd_anterior.toLocaleString('pt-BR')} → ${r.qtd_nova.toLocaleString('pt-BR')}`];
      if (r.custo_anterior != null) partes.push(`custo ${formatMoeda(r.custo_anterior)} → ${formatMoeda(r.custo_novo)}`);
      if (r.vinculos_corrigidos > 0) partes.push('Qt.Emb do fornecedor corrigida');
      toast.sucesso(partes.join(' · '));

      // ⚠️ O estoque e a metade que esta porta NAO faz. O recado dele nao pode ir num toast de 4s:
      // quem vai acertar na ficha precisa do numero na mao, e ninguem pode pedir replay de um
      // toast. Fica na linha do item ate a proxima carga da tela.
      if (r.estoque_atual != null && Math.abs(r.estoque_delta_sugerido) > 0.0001) {
        setPendenciaEstoque((prev) => ({
          ...prev,
          [itemId]: {
            produtoId: r.produto_id,
            codigo: r.codigo_interno,
            estoqueHoje: r.estoque_atual!,
            qtdAnterior: r.qtd_anterior,
            qtdNova: r.qtd_nova,
            delta: r.estoque_delta_sugerido,
          },
        }));
      }
    } catch (e: any) {
      toast.erro(e?.message || 'Nao foi possivel corrigir a quantidade.');
    } finally {
      setSalvandoQtd(false);
    }
  }

  // Sai pra outra tela deixando a nota marcada na URL (?nota=): a seta ← de la volta pelo
  // historico e o deep-link acima reabre a nota expandida, em vez da lista toda fechada.
  function sairMarcandoNota(notaId: number, destino: string) {
    const params = new URLSearchParams(searchParams);
    params.set('nota', String(notaId));
    navigate(`/notas-entrada?${params}`, { replace: true });
    navigate(destino);
  }
  const abrirProduto = (notaId: number, produtoId: number) => sairMarcandoNota(notaId, `/produtos/${produtoId}`);
  // O lapis do item abre a linha do CATALOGO DO FORNECEDOR (pedido dele, 29/10/2026), nao a ficha
  // do produto: e' la que se ajusta embalagem, Qt.Emb, codigo alternativo e vinculo.
  const abrirNoCatalogo = (notaId: number, fornecedorId: number, codigo: string) =>
    sairMarcandoNota(notaId, `/catalogo-fornecedor?${new URLSearchParams({ fornecedor: String(fornecedorId), codigo })}`);

  function toggleExpand(notaId: number) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(notaId)) next.delete(notaId);
      else {
        next.add(notaId);
        if (itensPorNota[notaId] === undefined) void carregarItensDaNota(notaId);
      }
      return next;
    });
  }

  async function excluirNota(e: React.MouseEvent, nota: Nota) {
    e.stopPropagation();
    if (!confirm(`Excluir nota ${nota.numero}? ${nota.estoque_atualizado ? 'O estoque sera revertido.' : ''}`)) return;
    setExcluindo(nota.id);
    try {
      await api.delete(`/api/notas-entrada/${nota.id}`);
      setNotas(prev => prev.filter(n => n.id !== nota.id));
    } catch (err: any) {
      toast.erro(err.message || 'Erro ao excluir nota');
    } finally {
      setExcluindo(null);
    }
  }

  const sticky = useStickyHeader();

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">Notas de Entrada</h2>
          <div className="pagina-acoes">
            {selecionadas.size > 0 && (
              <button
                onClick={() => {
                  if (!rateio.pronto) { toast.erro(rateio.motivo); return; }
                  const ids = rateio.escolhidas.map((n) => n.id).join(',');
                  navigate(`/notas-entrada/ratear?ids=${ids}`);
                }}
                disabled={!rateio.pronto}
                title={rateio.pronto
                  ? 'Somar estas notas como UMA compra e recalcular o custo dos produtos'
                  : rateio.motivo}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#fde68a', color: '#000', fontWeight: 600 }}
              >
                <Scale size={16} /> Ratear compra ({selecionadas.size})
              </button>
            )}
            <button
              onClick={() => navigate('/notas-entrada/importar')}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition text-sm"
            >
              <Upload size={16} /> Importar XML
            </button>
          </div>
        </div>
      </div>

      {soDivergencia ? (
        <div className="mb-2 mt-2 flex items-center gap-3 text-sm rounded px-3 py-2" style={{ background: '#fde68a', color: '#000' }}>
          <span>
            Mostrando so as notas com <strong>item que faltou</strong> ainda sem finalizar ({notasVisiveis.length}).
          </span>
          <button type="button" className="underline font-semibold" onClick={() => navigate('/notas-entrada')}>
            Ver todas as notas
          </button>
        </div>
      ) : (
        <div className="text-sm muted mb-2 mt-2">
          {notas.length} nota{notas.length !== 1 ? 's' : ''} importada{notas.length !== 1 ? 's' : ''}
        </div>
      )}

      <div className="rounded-lg border" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--card-bg)' }}>
        {carregando ? (
          <div className="p-8 text-center muted">Carregando...</div>
        ) : notas.length === 0 ? (
          <div className="p-8 text-center muted">
            <FileText size={40} className="mx-auto mb-2 opacity-30" />
            Nenhuma nota importada ainda
          </div>
        ) : (
          <div>
            <table className="w-full text-sm" style={sticky.tableStyle}>
              <thead className="grid-header">
                <tr>
                  <th className="w-8 text-center" style={sticky.thStyle} title="Marque as notas de uma mesma compra pra ratear o custo"></th>
                  <th className="w-10" style={sticky.thStyle}></th>
                  <th className="text-left" style={sticky.thStyle}>Numero</th>
                  <th className="text-left" style={sticky.thStyle}>Fornecedor</th>
                  <th className="text-center w-28" style={sticky.thStyle}>Data Emissao</th>
                  <th className="text-right w-28" style={sticky.thStyle}>Valor Total</th>
                  <th className="text-center w-24" style={sticky.thStyle}>Estoque</th>
                  <th className="text-center w-28" style={sticky.thStyle}>Importado em</th>
                  <th className="text-center w-16" style={sticky.thStyle}></th>
                </tr>
              </thead>
              <tbody className="grid-body">
                {notasVisiveis.map((n) => {
                  const expandido = expandedIds.has(n.id);
                  const itens = itensPorNota[n.id];
                  const dn = dadosNota[n.id] || {};
                  return (
                    <Fragment key={n.id}>
                      <tr
                        ref={(el) => { notaRefs.current[n.id] = el; }}
                        className="border-b cursor-pointer"
                        onClick={() => {
                          try { localStorage.setItem(ULTIMA_REVISADA_KEY, String(n.id)); } catch {}
                          setUltimaRevisadaId(n.id);
                        }}
                        onDoubleClick={() => abrirRevisarMargens(n.id)}
                        // Linha inteira em 12px preto (pedido dele, 04/11/2026) — serie, CNPJ, UF,
                        // selos e "importado em" deixaram o cinza/text-xs pra herdar daqui.
                        style={ultimaRevisadaId === n.id
                          ? { backgroundColor: '#fde68a', color: '#000', fontSize: 12 }
                          : { color: '#000', fontSize: 12 }}
                      >
                        <td className="px-1 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selecionadas.has(n.id)}
                            onChange={() => alternarSelecao(n.id)}
                            disabled={!n.estoque_atualizado}
                            title={n.estoque_atualizado
                              ? 'Marcar como parte de uma compra a ratear'
                              : 'Esta nota nao atualizou o estoque — nao gravou custo a corrigir'}
                            style={{ width: 15, height: 15, accentColor: 'var(--primary)', cursor: 'pointer' }}
                          />
                        </td>
                        <td className="px-2 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => toggleExpand(n.id)}
                            className="inline-flex items-center justify-center w-6 h-6 rounded border text-blue-600 hover:bg-blue-50"
                            style={{ borderColor: 'var(--input-border)' }}
                            title={expandido ? 'Ocultar itens' : 'Ver itens'}
                          >
                            {expandido ? <Minus size={14} /> : <Plus size={14} />}
                          </button>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="font-medium">{n.numero}</div>
                          <div>Serie {n.serie}</div>
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="font-medium">{n.fornecedor_fantasia || n.fornecedor_razao || n.fornecedor_nome}</div>
                          <div>
                            {n.fornecedor_cnpj}
                            {n.fornecedor_uf && (
                              <span
                                style={{ marginLeft: 8, fontWeight: 700 }}
                                title="UF da filial que emitiu a nota (e a que decide o DIFAL)"
                              >
                                {n.fornecedor_uf}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-center">{formatDataSP(n.data_emissao)}</td>
                        <td className="px-3 py-2.5 text-right font-medium">
                          {formatMoeda((parseFloat(n.valor_total) || 0) + (parseFloat(n.outras_despesas || '0') || 0))}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <div className="flex flex-col items-center gap-1">
                            {n.estoque_atualizado ? (
                              <span className="inline-flex items-center gap-1 bg-green-100 px-2 py-0.5 rounded-full">
                                <CheckCircle size={12} /> Atualizado
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded-full">
                                <Clock size={12} /> Pendente
                              </span>
                            )}
                            {n.tem_divergencia && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
                                style={{ backgroundColor: '#fde68a' }}
                                title="Esta nota tem itens com divergencia de recebimento"
                              >
                                <AlertTriangle size={12} /> Divergencia
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-center">{formatDataSP(n.criado_em)}</td>
                        <td className="px-3 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => abrirRevisarMargens(n.id)}
                              className="p-1.5 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="Revisar margens dos produtos"
                            >
                              <Percent size={15} />
                            </button>
                            {/* [DA-559] So aparece com o estoque ja atualizado: a mercadoria precisa
                                ter ENTRADO antes de poder sair, senao o estoque fica negativo. */}
                            <button
                              onClick={() => setDevolverNotaId(n.id)}
                              disabled={!n.estoque_atualizado}
                              className="p-1.5 hover:bg-amber-50 rounded-lg transition disabled:opacity-30"
                              style={{ color: n.estoque_atualizado ? '#b45309' : undefined }}
                              title={n.estoque_atualizado
                                ? 'Devolver mercadoria ao fornecedor (emite NF-e de saida)'
                                : 'Conclua a entrada no estoque antes de devolver'}
                            >
                              <Undo2 size={15} />
                            </button>
                            <button
                              onClick={(e) => excluirNota(e, n)}
                              disabled={excluindo === n.id}
                              className="p-1.5 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
                              title="Excluir nota"
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandido && (
                        <tr style={{ backgroundColor: 'var(--card-bg, #f0f7ff)' }}>
                          <td colSpan={9} className="p-0">
                            <div className="px-8 py-3">
                              {itens === undefined || itens === null ? (
                                <div className="py-4 text-center text-sm muted">Carregando itens...</div>
                              ) : itens.length === 0 ? (
                                <div className="py-4 text-center text-sm muted">Nenhum item</div>
                              ) : (
                                <>
                                  <div className="flex items-center justify-between gap-6 mb-2 text-xs muted">
                                    <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setDivergenciaNotaId(n.id); }}
                                      className="inline-flex items-center px-3 py-1 rounded text-xs font-semibold"
                                      style={{ border: '1px solid var(--primary)', color: 'var(--primary)', backgroundColor: 'var(--panel-bg)', whiteSpace: 'nowrap' }}
                                      title="Registrar itens da nota que nao chegaram"
                                    >
                                      Se Faltou Item Nesta Nota, Registre Aqui
                                    </button>
                                    {/* Encerra so a PENDENCIA (sem mexer em estoque/credito) — admin, e so
                                        quando ha divergencia aberta: nas outras nao ha o que finalizar. */}
                                    {ehAdmin && (n.divergencias_abertas ?? 0) > 0 && (
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); void finalizarDivergencia(n); }}
                                        disabled={finalizando === n.id}
                                        className="btn-primario inline-flex items-center px-3 py-1 rounded text-xs font-semibold disabled:opacity-50"
                                        style={{ whiteSpace: 'nowrap' }}
                                        title="Encerrar a pendencia das divergencias desta nota (estoque e credito ficam como estao)"
                                      >
                                        {finalizando === n.id ? 'Finalizando...' : `Finalizar Divergencia (${n.divergencias_abertas})`}
                                      </button>
                                    )}
                                    </div>
                                    {(parseFloat(dn.outras_despesas || '0') || 0) > 0 && (
                                      <div className="flex gap-6">
                                        <span>Total da Nota Fiscal: <strong>{formatMoeda(dn.valor_total || 0)}</strong></span>
                                        <span>Outras despesas: <strong>{formatMoeda(dn.outras_despesas || 0)}</strong></span>
                                      </div>
                                    )}
                                  </div>
                                  <table className="w-full text-sm rounded-lg border overflow-hidden" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--card-bg)' }}>
                                    <thead className="grid-subheader">
                                      <tr>
                                        <th className="text-center w-8">#</th>
                                        {/* Largura fixa: no automatico a coluna encolhia ate a foto e o codigo vizinho ficava
                                            sob ela no navegador dele (04/11/2026). Foto de 50px = a do Cadastro de Produtos. */}
                                        <th className="text-center" style={{ width: 64, minWidth: 64 }}>Foto</th>
                                        <th className="text-left w-16">Cod.</th>
                                        <th className="text-left">Produto</th>
                                        <th className="text-left w-20">Cod. Forn.</th>
                                        <th className="text-left">Descricao NF</th>
                                        <th className="text-center w-20">Emb.</th>
                                        <th className="text-right w-16">Qtd</th>
                                        <th className="text-right w-24">V. Frac.</th>
                                        <th className="text-right w-16">ST</th>
                                        <th className="text-right w-14">Aliq.</th>
                                        <th className="text-right w-20">DIFAL</th>
                                        <th className="text-right w-24">Custo Real</th>
                                        <th className="text-right w-20" title="Estoque atual do produto (hoje), na mesma unidade da coluna Qtd">Estoque</th>
                                        <th className="w-8"></th>
                                      </tr>
                                    </thead>
                                    <tbody className="grid-body">
                                      {(() => {
                                        const totalNF = (parseFloat(dn.valor_total || '0') || 0) + (parseFloat(dn.outras_despesas || '0') || 0);
                                        const somaXml = parseFloat(dn.valor_produtos || '0') || itens.reduce((s, i) => s + (parseFloat(i.valor_total) || 0), 0);
                                        const totalIPI = itens.reduce((s, i) => s + (parseFloat(i.valor_ipi) || 0), 0);
                                        // [DA-734] A conta do custo passou a sair da peca unica do
                                        // `shared` — a mesma que o backend usa pra GRAVAR. Antes
                                        // esta tela somava ST + DIFAL e a importacao usava um OU
                                        // outro: medido em 21/09, nenhum item da casa trouxe os
                                        // dois juntos (0 de 565), entao os numeros coincidiam e a
                                        // divergencia ficou escondida.
                                        const fatorSh = fatorRateioDaNota({ totalNF, somaXml, totalIPI });
                                        return itens.map((it) => {
                                          const qtdReal = parseFloat(it.qtd_real || '') || parseFloat(it.quantidade) || 1;
                                          const vTot = parseFloat(it.valor_total) || 0;
                                          const vFrac = vTot / qtdReal;
                                          const stUnit = qtdReal > 0 ? (parseFloat(it.valor_icms_st) || 0) / qtdReal : 0;
                                          const difalUnit = qtdReal > 0 ? (parseFloat(it.valor_difal) || 0) / qtdReal : 0;
                                          const argsCusto = {
                                            valorTotal: vTot,
                                            valorIpi: parseFloat(it.valor_ipi) || 0,
                                            valorDifal: parseFloat(it.valor_difal) || 0,
                                            valorSt: parseFloat(it.valor_icms_st) || 0,
                                            fatorRateio: fatorSh,
                                            valorUnitario: parseFloat(it.valor_unitario) || 0,
                                          };
                                          const custoReal = custoRealDoItem({ ...argsCusto, qtdReal });
                                          // [DA-559] Item que ja voltou pro fornecedor. A linha
                                          // continua legivel (o custo dela ainda explica o rateio da
                                          // nota) mas para de parecer mercadoria em casa: esmaece e
                                          // diz por qual nota saiu.
                                          const devolvido = Number(it.qtd_devolvida || 0) > 0;
                                          // [DA-734] So admin corrige (e porta de preco de custo,
                                          // como o rateio), e item ja devolvido fica de fora: a
                                          // NF-e de devolucao saiu com o custo de agora.
                                          const podeCorrigir = ehAdmin && !devolvido;
                                          const editando = editandoQtd?.itemId === it.id;
                                          const qtdDigitada = editando ? Number(String(editandoQtd.texto).replace(',', '.')) : NaN;
                                          const qtdValida = Number.isFinite(qtdDigitada) && qtdDigitada > 0;
                                          const custoNovo = qtdValida ? custoRealDoItem({ ...argsCusto, qtdReal: qtdDigitada }) : null;
                                          const pend = pendenciaEstoque[it.id];
                                          return (
                                            <Fragment key={it.id}>
                                            <tr
                                              ref={(el) => { itemRefs.current[it.id] = el; }}
                                              className="border-b cursor-pointer"
                                              style={{
                                                ...(itemSel === it.id ? { backgroundColor: 'var(--menu-ativo-bg)' } : {}),
                                                ...(devolvido && itemSel !== it.id
                                                  ? { backgroundColor: 'var(--panel-bg)', opacity: 0.62 }
                                                  : {}),
                                              }}
                                              onClick={(e) => { e.stopPropagation(); setItemSel(itemSel === it.id ? null : it.id); }}
                                            >
                                              <td className="px-2 py-1.5 text-center text-[14px]" style={{ color: '#000' }}>{it.numero_item}</td>
                                              <td className="px-1 py-1.5 text-center" style={{ width: 64, minWidth: 64 }}>
                                                {it.foto_url
                                                  // Clique amplia (pedido dele, 04/11/2026) — o mesmo LightboxImagem do Orcamento.
                                                  ? <FotoProduto
                                                      url={it.foto_url}
                                                      tam={50}
                                                      style={{ margin: '0 auto', cursor: 'zoom-in' }}
                                                      title="Clique para ampliar a foto"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setFotoAmpliada({ url: it.foto_url!, legenda: `${it.codigo_interno ?? ''} — ${it.produto_descricao || it.descricao || ''}` });
                                                      }}
                                                    />
                                                  : <span className="opacity-40 text-xs">—</span>}
                                              </td>
                                              {/* Do Cod. ao Estoque: 14px preto (pedido dele, 04/11/2026). */}
                                              <td className="px-2 py-1.5 codigo-curto text-[14px]" style={{ color: '#000' }}>{it.codigo_interno || '—'}</td>
                                              <td className="px-2 py-1.5 text-[14px] font-medium truncate max-w-[180px]" style={{ color: '#000' }} title={it.produto_descricao || ''}>
                                                {it.produto_descricao || '—'}
                                              </td>
                                              <td className="px-2 py-1.5 text-[14px]" style={{ color: '#000' }}>{it.codigo_fornecedor || '—'}</td>
                                              <td className="px-2 py-1.5 text-[14px] max-w-[200px]" style={{ color: '#000' }} title={it.descricao || ''}>
                                                <div className="truncate" style={devolvido ? { textDecoration: 'line-through' } : undefined}>
                                                  {it.descricao || '—'}
                                                </div>
                                                {/* O selo diz o QUE aconteceu e POR ONDE — "devolvido"
                                                    sozinho manda procurar em outra tela. */}
                                                {devolvido && (
                                                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 2, fontSize: 10, fontWeight: 700, color: '#fff', background: '#b45309', borderRadius: 4, padding: '1px 5px', whiteSpace: 'nowrap' }}>
                                                    <Undo2 size={9} />
                                                    DEVOLVIDO AO FORNECEDOR · NF-e {String(it.devolucao_nfe_numero ?? '').padStart(6, '0')}
                                                    {it.devolucao_em ? ` · ${formatDataSP(it.devolucao_em)}` : ''}
                                                  </div>
                                                )}
                                              </td>
                                              <td className="px-2 py-1.5 text-center text-[14px]" style={{ color: '#000' }}>{it.unidade_venda_sigla || it.unidade || '—'}</td>
                                              {/* [DA-734] A Qtd e' o unico numero desta grade que
                                                  o operador pode ter errado na importacao — e ate
                                                  aqui o conserto era excluir a nota inteira. */}
                                              <td className="px-2 py-1.5 text-right text-[14px]" style={{ color: '#000' }}>
                                                {editando ? (
                                                  <input
                                                    autoFocus
                                                    value={editandoQtd.texto}
                                                    onClick={(e) => e.stopPropagation()}
                                                    onChange={(e) => setEditandoQtd({ ...editandoQtd, texto: e.target.value })}
                                                    onKeyDown={(e) => {
                                                      e.stopPropagation();
                                                      if (e.key === 'Enter' && qtdValida && !salvandoQtd) void aplicarQtd(n.id, it.id, qtdDigitada);
                                                      if (e.key === 'Escape') setEditandoQtd(null);
                                                    }}
                                                    className="w-20 px-1 py-0.5 text-right text-xs rounded border"
                                                    style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--input-bg)', color: 'var(--text)' }}
                                                  />
                                                ) : podeCorrigir ? (
                                                  <button
                                                    type="button"
                                                    title="Corrigir a quantidade desta linha"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setEditandoQtd({ itemId: it.id, notaId: n.id, texto: String(qtdReal) });
                                                    }}
                                                    className="inline-flex items-center gap-1 hover:underline"
                                                    style={{ color: 'inherit' }}
                                                  >
                                                    {Number(it.qtd_real || it.quantidade).toLocaleString('pt-BR')}
                                                    <Pencil size={10} className="opacity-40" />
                                                  </button>
                                                ) : (
                                                  Number(it.qtd_real || it.quantidade).toLocaleString('pt-BR')
                                                )}
                                              </td>
                                              <td className="px-2 py-1.5 text-right text-[14px]" style={{ color: '#000' }}>{formatMoeda(vFrac)}</td>
                                              <td className="px-2 py-1.5 text-right text-[14px]" style={{ color: '#000' }}>{stUnit > 0 ? formatMoeda(stUnit) : <span className="opacity-30">—</span>}</td>
                                              <td className="px-2 py-1.5 text-right text-[14px]" style={{ color: '#000' }}>{parseFloat(it.aliquota_icms) > 0 ? `${Number(it.aliquota_icms).toLocaleString('pt-BR')}%` : <span className="opacity-30">—</span>}</td>
                                              <td className="px-2 py-1.5 text-right text-[14px]" style={{ color: '#000' }}>{difalUnit > 0 ? formatMoeda(difalUnit) : <span className="opacity-30">—</span>}</td>
                                              <td className={`px-2 py-1.5 text-right text-[14px] font-bold ${itemSel === it.id ? '' : 'bg-green-50'}`} style={{ color: '#000' }}>{formatMoeda(custoReal)}</td>
                                              <td className="px-2 py-1.5 text-right text-[14px]" style={itemSel === it.id ? { color: '#000' } : { backgroundColor: '#fde68a', color: '#000' }}>
                                                {it.produto_estoque_atual != null ? Number(it.produto_estoque_atual).toLocaleString('pt-BR') : <span className="opacity-30">—</span>}
                                              </td>
                                              <td className="px-1 py-1.5 text-center">
                                                {n.fornecedor_id && it.codigo_fornecedor && (
                                                  <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); abrirNoCatalogo(n.id, n.fornecedor_id!, it.codigo_fornecedor!); }}
                                                    className="p-1 hover:text-blue-600 hover:bg-blue-50 rounded"
                                                    title="Editar este item no Catalogo do fornecedor"
                                                  >
                                                    <Pencil size={14} />
                                                  </button>
                                                )}
                                              </td>
                                            </tr>

                                            {/* [DA-734] A previa acompanha a digitacao: o custo
                                                novo aparece ANTES de aplicar, e e' calculado pela
                                                mesma peca que o backend usa pra gravar. */}
                                            {editando && (
                                              <tr>
                                                <td colSpan={15} className="px-3 py-2" style={{ backgroundColor: 'var(--panel-bg)' }}>
                                                  <div className="text-xs" style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center' }}>
                                                    <span style={{ fontWeight: 700 }}>
                                                      Item {it.numero_item} · {it.codigo_interno || '—'} {it.produto_descricao || ''}
                                                    </span>
                                                    {qtdValida ? (
                                                      <>
                                                        {/* 4 casas, nao `formatMoeda`: o custo tem 4 no banco, e num item
                                                            barato por metro as duas ultimas sao a diferenca inteira —
                                                            arredondar aqui esconderia justamente o que a previa existe
                                                            pra mostrar (0,1545 e 1,5454 viravam "R$ 0,15" e "R$ 1,55"). */}
                                                        <span>
                                                          Custo unitario:{' '}
                                                          <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{moeda4(custoReal)}</span>
                                                          {' → '}
                                                          <span style={{ fontWeight: 700 }}>{moeda4(custoNovo!)}</span>
                                                        </span>
                                                        {it.codigo_fornecedor && Number(it.quantidade) > 0 && (
                                                          <span className="muted">
                                                            Qt.Emb do fornecedor ({it.codigo_fornecedor}):{' '}
                                                            {(qtdDigitada / Number(it.quantidade)).toLocaleString('pt-BR')}
                                                          </span>
                                                        )}
                                                      </>
                                                    ) : (
                                                      <span style={{ color: '#b45309', fontWeight: 600 }}>
                                                        Informe uma quantidade maior que zero.
                                                      </span>
                                                    )}
                                                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                                      <button type="button" className="btn-secundario" style={{ padding: '3px 10px', fontSize: 12 }}
                                                        onClick={(e) => { e.stopPropagation(); setEditandoQtd(null); }}>
                                                        Cancelar
                                                      </button>
                                                      <button type="button" className="btn-primario" style={{ padding: '3px 12px', fontSize: 12 }}
                                                        disabled={!qtdValida || salvandoQtd || qtdDigitada === qtdReal}
                                                        onClick={(e) => { e.stopPropagation(); void aplicarQtd(n.id, it.id, qtdDigitada); }}>
                                                        {salvandoQtd ? 'Aplicando...' : 'Aplicar'}
                                                      </button>
                                                    </span>
                                                  </div>
                                                  {/* O botao desabilitado DIZ por que. */}
                                                  {qtdValida && qtdDigitada === qtdReal && (
                                                    <div className="text-xs muted" style={{ marginTop: 4 }}>
                                                      E a mesma quantidade que ja esta gravada.
                                                    </div>
                                                  )}
                                                  <div className="text-xs muted" style={{ marginTop: 5 }}>
                                                    Isto corrige a NOTA, o custo do produto e a Qt.Emb guardada no fornecedor.
                                                    O <strong>estoque nao muda aqui</strong> — hoje o produto esta com{' '}
                                                    {it.produto_estoque_atual != null ? Number(it.produto_estoque_atual).toLocaleString('pt-BR') : '—'}.
                                                  </div>
                                                </td>
                                              </tr>
                                            )}

                                            {/* A metade que esta porta nao faz, dita com o numero
                                                na mao e com a porta pra resolver ao lado. */}
                                            {pend && (
                                              <tr>
                                                <td colSpan={15} className="px-3 py-2">
                                                  <div style={{
                                                    display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
                                                    background: '#fde68a', color: '#000', borderRadius: 6, padding: '6px 10px', fontSize: 12,
                                                  }}>
                                                    <AlertTriangle size={14} />
                                                    <span>
                                                      <strong>Falta acertar o estoque.</strong> A nota lancou{' '}
                                                      {pend.qtdAnterior.toLocaleString('pt-BR')} e agora diz {pend.qtdNova.toLocaleString('pt-BR')} —
                                                      diferenca de {pend.delta > 0 ? '+' : ''}{pend.delta.toLocaleString('pt-BR')}.
                                                      O produto esta com <strong>{pend.estoqueHoje.toLocaleString('pt-BR')}</strong>.
                                                    </span>
                                                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                                      {pend.produtoId && (
                                                        <button type="button" className="btn-primario" style={{ padding: '3px 10px', fontSize: 12 }}
                                                          onClick={(e) => { e.stopPropagation(); abrirProduto(n.id, pend.produtoId!); }}>
                                                          Abrir a ficha
                                                        </button>
                                                      )}
                                                      <button type="button" className="btn-secundario" style={{ padding: '3px 10px', fontSize: 12, borderColor: '#00000033', color: '#000' }}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setPendenciaEstoque((prev) => { const p = { ...prev }; delete p[it.id]; return p; });
                                                        }}>
                                                        Ja acertei
                                                      </button>
                                                    </span>
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                            </Fragment>
                                          );
                                        });
                                      })()}
                                    </tbody>
                                  </table>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {divergenciaNotaId !== null && (() => {
        const nota = notas.find((n) => n.id === divergenciaNotaId);
        const itens = itensPorNota[divergenciaNotaId];
        if (!nota || !itens) return null;
        return (
          <ModalDivergenciaRecebimento
            notaId={divergenciaNotaId}
            numeroNota={nota.numero}
            itens={itens}
            onFechar={() => setDivergenciaNotaId(null)}
            onGravado={() => {
              setNotas((prev) => prev.map((n) => (n.id === divergenciaNotaId ? { ...n, tem_divergencia: true } : n)));
              // Rele a contagem de abertas (botao Finalizar) e avisa a pastilha do cabecalho.
              void carregarNotas();
              window.dispatchEvent(new Event('divergencias-recebimento:mudou'));
            }}
          />
        );
      })()}

      {devolverNotaId !== null && (() => {
        const nota = notas.find((n) => n.id === devolverNotaId);
        if (!nota) return null;
        return (
          <ModalDevolverAoFornecedor
            notaId={devolverNotaId}
            numeroNota={nota.numero}
            fornecedorNome={nota.fornecedor_fantasia || nota.fornecedor_razao || nota.fornecedor_nome}
            onFechar={() => setDevolverNotaId(null)}
            onEmitida={() => { void carregarItensDaNota(devolverNotaId); }}
          />
        );
      })()}

      {fotoAmpliada && (
        <LightboxImagem
          imagens={[{ url: fotoAmpliada.url, legenda: fotoAmpliada.legenda }]}
          onFechar={() => setFotoAmpliada(null)}
        />
      )}
    </div>
  );
}
